const TOKEN_MAX_AGE_MS = 4 * 60 * 1000; // 4 min (safety buffer before Turnstile's 5-min expiry)
const TOKEN_WAIT_TIMEOUT_MS = 30 * 1000;

type TokenWaiter = {
    resolve: (token: string | null) => void;
    timer: ReturnType<typeof setTimeout>;
    startedAt: number;
};

let currentToken: string | null = null;
let tokenTimestamp = 0;
let widgetId: string | null = null;
let initialized = false;
let tokenWaiters: TokenWaiter[] = [];
let isResetting = false;
let lastEvent = 'not_initialized';
let lastErrorCode: string | null = null;
let lastWaitMs: number | null = null;

function flushWaiters(token: string): void {
    const waiters = tokenWaiters;
    tokenWaiters = [];
    for (const waiter of waiters) {
        clearTimeout(waiter.timer);
        lastWaitMs = Date.now() - waiter.startedAt;
        waiter.resolve(token);
    }
}

function resetWidget(reason = 'reset'): void {
    if (isResetting) {
        return;
    }
    isResetting = true;
    try {
        lastEvent = reason;
        currentToken = null;
        if (widgetId && window.turnstile) {
            const container = document.getElementById('turnstile-container');
            if (container) {
                try {
                    window.turnstile.reset(widgetId);
                } catch {
                    // Widget was already invalidated internally by Turnstile.
                    // Remove and re-render a fresh widget.
                    try {
                        window.turnstile.remove(widgetId);
                    } catch {
                        // remove can also fail if widget is fully gone — ignore
                    }
                    container.remove();
                    widgetId = null;
                    renderWidget();
                }
            } else {
                widgetId = null;
                renderWidget();
            }
        }
    } finally {
        isResetting = false;
    }
}

function isTokenStale(): boolean {
    return !currentToken || Date.now() - tokenTimestamp > TOKEN_MAX_AGE_MS;
}

let visibilityListenerAdded = false;

function onVisibilityChange(): void {
    if (document.visibilityState === 'visible' && isTokenStale()) {
        resetWidget('visibility_stale');
    }
}

function renderWidget(): void {
    const siteKey = window.TURNSTILE_SITE_KEY;
    if (!siteKey) {
        lastEvent = 'site_key_missing';
        return;
    }
    if (!window.turnstile) {
        lastEvent = 'script_not_loaded';
        return;
    }

    const container = document.createElement('div');
    container.id = 'turnstile-container';
    container.style.cssText = 'position:fixed;right:24px;bottom:24px;z-index:2147483647';
    document.body.appendChild(container);

    lastEvent = 'rendered';
    widgetId = window.turnstile.render(container, {
        sitekey: siteKey,
        appearance: 'interaction-only',
        callback: (token: string) => {
            lastEvent = 'token_received';
            lastErrorCode = null;
            currentToken = token;
            tokenTimestamp = Date.now();
            flushWaiters(token);
        },
        'expired-callback': () => resetWidget('expired'),
        'timeout-callback': () => resetWidget('challenge_timeout'),
        'unsupported-callback': () => {
            lastEvent = 'unsupported';
        },
        'error-callback': (errorCode?: string) => {
            lastEvent = 'error_callback';
            lastErrorCode = errorCode || null;
            // No-op: Turnstile auto-retries and the success callback will
            // deliver a valid token. Clearing here would discard a still-usable
            // token from a previous successful challenge.
        },
    });

    if (!visibilityListenerAdded) {
        visibilityListenerAdded = true;
        document.addEventListener('visibilitychange', onVisibilityChange);
    }
}

/**
 * Initialize the Turnstile widget. Safe to call from any entry point —
 * handles missing site key, waits for the async script to load, and
 * no-ops if already initialized.
 */
export function initTurnstile(): void {
    if (!window.TURNSTILE_SITE_KEY) {
        lastEvent = 'site_key_missing';
        return;
    }
    if (initialized) {
        return;
    }
    initialized = true;

    if (window.turnstile) {
        renderWidget();
    } else {
        lastEvent = 'waiting_for_script';
        const interval = setInterval(() => {
            if (window.turnstile) {
                clearInterval(interval);
                renderWidget();
            }
        }, 200);
    }
}

export function getTurnstileToken(): string | null {
    if (currentToken && Date.now() - tokenTimestamp > TOKEN_MAX_AGE_MS) {
        resetWidget('token_stale');
    }
    return currentToken;
}

/**
 * Async version of getTurnstileToken that waits for a valid token.
 * Returns immediately if a valid token exists; otherwise queues a waiter
 * that resolves when the widget callback fires (or times out).
 */
export function awaitTurnstileToken(timeoutMs = TOKEN_WAIT_TIMEOUT_MS): Promise<string | null> {
    if (!window.TURNSTILE_SITE_KEY) {
        lastEvent = 'site_key_missing';
        return Promise.resolve(null);
    }

    if (!initialized) {
        lastEvent = 'not_initialized';
        return Promise.resolve(null);
    }

    if (currentToken && !isTokenStale()) {
        lastEvent = 'token_ready';
        return Promise.resolve(currentToken);
    }

    if (currentToken) {
        resetWidget('token_stale');
    }

    return new Promise<string | null>((resolve) => {
        const startedAt = Date.now();
        lastEvent = 'wait_started';
        lastWaitMs = null;
        const timer = setTimeout(() => {
            tokenWaiters = tokenWaiters.filter((w) => w.resolve !== resolve);
            lastEvent = 'wait_timeout';
            lastWaitMs = Date.now() - startedAt;
            resolve(null);
        }, timeoutMs);

        tokenWaiters.push({ resolve, timer, startedAt });
    });
}

function sanitizeDiagnosticValue(value: string): string {
    return value.replace(/[^a-zA-Z0-9_.:-]/g, '_').slice(0, 80);
}

export function getTurnstileDiagnosticHeader(): string {
    const tokenIsStale = Boolean(currentToken && isTokenStale());
    const parts = [
        'v=1',
        `init=${initialized ? 1 : 0}`,
        `sitekey=${window.TURNSTILE_SITE_KEY ? 1 : 0}`,
        `script=${window.turnstile ? 1 : 0}`,
        `widget=${widgetId ? 1 : 0}`,
        `token=${currentToken && !tokenIsStale ? 1 : 0}`,
        `stale=${tokenIsStale ? 1 : 0}`,
        `waiters=${tokenWaiters.length}`,
        `event=${sanitizeDiagnosticValue(lastEvent)}`,
    ];

    if (lastWaitMs !== null) {
        parts.push(`wait_ms=${Math.round(lastWaitMs)}`);
    }
    if (lastErrorCode) {
        parts.push(`error=${sanitizeDiagnosticValue(lastErrorCode)}`);
    }

    return parts.join(';');
}

/** Check if a reset is in progress — only for use in tests. */
export function _isResettingForTesting(): boolean {
    return isResetting;
}

/** Reset all module state — only for use in tests. */
export function _resetForTesting(): void {
    currentToken = null;
    tokenTimestamp = 0;
    widgetId = null;
    initialized = false;
    isResetting = false;
    lastEvent = 'not_initialized';
    lastErrorCode = null;
    lastWaitMs = null;
    if (visibilityListenerAdded) {
        document.removeEventListener('visibilitychange', onVisibilityChange);
        visibilityListenerAdded = false;
    }
    for (const waiter of tokenWaiters) {
        clearTimeout(waiter.timer);
    }
    tokenWaiters = [];
}
